import { useState, ReactNode, useMemo, useCallback } from 'react';
import { SearchFilters, OfferSearchResult } from '../../features/search/types/search.types';
import { SearchContext, SearchContextState, MapViewport } from './SearchContext';

const initialState: SearchContextState = {
  filters: null,
  results: [],
  scrollPosition: 0,
  totalResults: 0,
  viewMode: 'list',
  mapViewport: null,
};

export function SearchContextProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [context, setContext] = useState<SearchContextState>(initialState);

  const setSearchContext = useCallback(
    (filters: SearchFilters, results: OfferSearchResult[], totalResults: number) => {
      setContext(prev => ({
        ...prev,
        filters,
        results,
        scrollPosition: 0, // Reset scroll when new search
        totalResults,
      }));
    },
    []
  );

  const setScrollPosition = useCallback((position: number) => {
    setContext(prev => ({
      ...prev,
      scrollPosition: position,
    }));
  }, []);

  const setViewMode = useCallback((mode: 'list' | 'map') => {
    setContext(prev => ({
      ...prev,
      viewMode: mode,
    }));
  }, []);

  const setMapViewport = useCallback((viewport: MapViewport | null) => {
    setContext(prev => ({
      ...prev,
      mapViewport: viewport,
    }));
  }, []);

  const clearSearchContext = useCallback(() => {
    setContext(initialState);
  }, []);

  const getSearchContext = useCallback(() => context, [context]);

  const updateFavoriteInResults = useCallback((offerId: string, isFavorite: boolean) => {
    setContext(prev => ({
      ...prev,
      results: prev.results.map(offer => (offer.id === offerId ? { ...offer, isFavorite } : offer)),
    }));
  }, []);

  const value = useMemo(
    () => ({
      searchContext: context,
      setSearchContext,
      setScrollPosition,
      setViewMode,
      setMapViewport,
      clearSearchContext,
      getSearchContext,
      updateFavoriteInResults,
    }),
    [
      context,
      setSearchContext,
      setScrollPosition,
      setViewMode,
      setMapViewport,
      clearSearchContext,
      getSearchContext,
      updateFavoriteInResults,
    ]
  );

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
}
